import React, { Component } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import {
    AuthenticatedTemplate,
    UnauthenticatedTemplate,
} from "@azure/msal-react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Layout from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import StoreDetail from "./pages/StoreDetail";
import StoresList from "./pages/StoresList";
import Issues from "./pages/Issues";
import Reports from "./pages/Reports";
import ReportsSummary from "./pages/ReportsSummary";
import OpsOverview from "./pages/OpsOverview";
import Planograms from "./pages/Planograms";
import Settings from "./pages/Settings";
import Presets from "./pages/Presets";
import Infrastructure from "./pages/Infrastructure";
import StoreHourly from "./pages/StoreHourly";
import FridgeSurvey from "./pages/FridgeSurvey";
import StoreIssues from "./pages/StoreIssues";
import StoreReports from "./pages/StoreReports";
import VideoClips from "./pages/VideoClips";
import Reviews from "./pages/Reviews";
import ScanReviewPage from "./pages/ScanReviewPage";
import Admin from "./pages/Admin";
import EmailLog from "./pages/EmailLog";
import StoreOnboarding from "./pages/StoreOnboarding";
import DipMonitoring from "./pages/DipMonitoring";
import DipForm from "./pages/DipForm";
import DipFormDisabled from "./pages/DipFormDisabled";
import DipEvidence from "./pages/DipEvidence";
import DipReviewPage from "./pages/DipReviewPage";
import PosFraud from "./pages/PosFraud";
import FraudReport from "./pages/FraudReport";
import DipSafety from "./pages/DipSafety";
import AuditLog from "./pages/AuditLog";
import Monitoring from "./pages/Monitoring";
import HelpPage from "./pages/HelpPage";
import AssetScanner from "./pages/AssetScanner";
import AssetTypes from "./pages/AssetTypes";
import FixtureSchedules from "./pages/FixtureSchedules";
import AssetReview from "./pages/AssetReview";
import Contacts from "./pages/Contacts";
import Workers from "./pages/Workers";
import NvrConnectionGuide from "./pages/NvrConnectionGuide";
import Login from "./pages/Login";
import LoadingSpinner from "./components/LoadingSpinner";
import "./App.css";

// Order matters: first match becomes the landing page
const ROUTE_PERMISSIONS = [
    { path: "/", permission: "pages.dashboard" },
    { path: "/stores", permission: "pages.stores" },
    { path: "/issues", permission: "pages.issues" },
    { path: "/reports", permission: "pages.reports" },
    { path: "/video-clips", permission: "videos.view" },
    { path: "/settings", permission: "pages.settings" },
];

function DefaultRedirect() {
    const { hasPermission, loading } = useAuth();
    if (loading) return <LoadingSpinner message="Loading..." />;
    const first = ROUTE_PERMISSIONS.find(r => hasPermission(r.permission));
    if (first && first.path !== "/") return <Navigate to={first.path} replace />;
    // If they have dashboard permission or nothing at all, show dashboard
    return <Dashboard />;
}

function ProtectedRoute({ permission, children }) {
    const { hasPermission, loading } = useAuth();
    if (loading) return <LoadingSpinner message="Checking access..." />;
    if (permission && !hasPermission(permission)) {
        const first = ROUTE_PERMISSIONS.find(r => hasPermission(r.permission));
        return <Navigate to={first?.path || "/"} replace />;
    }
    return children;
}

class PageErrorBoundary extends Component {
    constructor(props) { super(props); this.state = { hasError: false, error: null }; }
    static getDerivedStateFromError(error) { return { hasError: true, error }; }
    render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: 24, maxWidth: 600, margin: "40px auto", textAlign: "center" }}>
                    <h2 style={{ color: "#dc2626" }}>Something went wrong</h2>
                    <p style={{ color: "#666" }}>{this.state.error?.message}</p>
                    <button onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
                        style={{ padding: "8px 20px", borderRadius: 6, border: "1px solid #ddd", cursor: "pointer", marginTop: 8 }}>
                        Reload page
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

function AppRoutes() {
    const { loading } = useAuth();
    if (loading) return <LoadingSpinner message="Loading..." />;

    return (
        <Layout>
            <Routes>
                <Route path="/" element={<DefaultRedirect />} />
                <Route path="/stores" element={<ProtectedRoute permission="pages.stores"><StoresList /></ProtectedRoute>} />
                <Route path="/store/:siteId" element={<ProtectedRoute permission="pages.stores"><StoreDetail /></ProtectedRoute>} />
                <Route path="/store/:siteId/hourly" element={<ProtectedRoute permission="pages.stores"><StoreHourly /></ProtectedRoute>} />
                <Route path="/fridge-survey" element={<ProtectedRoute permission="pages.stores"><FridgeSurvey /></ProtectedRoute>} />
                <Route path="/store/:siteId/issues" element={<ProtectedRoute permission="pages.stores"><StoreIssues /></ProtectedRoute>} />
                <Route path="/store/:siteId/reports" element={<ProtectedRoute permission="pages.reports"><StoreReports /></ProtectedRoute>} />
                <Route path="/issues" element={<ProtectedRoute permission="pages.issues"><Issues /></ProtectedRoute>} />
                <Route path="/reports" element={<ProtectedRoute permission="pages.reports"><Reports /></ProtectedRoute>} />
                <Route path="/reports/summary" element={<ProtectedRoute permission="pages.reports"><ReportsSummary /></ProtectedRoute>} />
                <Route path="/reports/ops" element={<ProtectedRoute permission="pages.reports"><OpsOverview /></ProtectedRoute>} />
                <Route path="/planograms" element={<ProtectedRoute permission="pages.planograms"><Planograms /></ProtectedRoute>} />
                <Route path="/presets" element={<ProtectedRoute permission="pages.presets"><Presets /></ProtectedRoute>} />
                <Route path="/onboarding" element={<ProtectedRoute permission="pages.onboarding"><StoreOnboarding /></ProtectedRoute>} />
                <Route path="/onboarding/:siteId" element={<ProtectedRoute permission="pages.onboarding"><StoreOnboarding /></ProtectedRoute>} />
                {/* Dip form temporarily disabled (2026-04-09) */}
                <Route path="/dip-form" element={<DipFormDisabled />} />
                <Route path="/dip-form/:siteId" element={<DipFormDisabled />} />
                <Route path="/dips" element={<ProtectedRoute permission="pages.infrastructure"><DipMonitoring /></ProtectedRoute>} />
                <Route path="/dips/:siteId" element={<ProtectedRoute permission="pages.infrastructure"><DipMonitoring /></ProtectedRoute>} />
                <Route path="/dip-evidence" element={<ProtectedRoute permission="pages.dip_evidence"><DipEvidence /></ProtectedRoute>} />
                <Route path="/posfraud" element={<ProtectedRoute permission="pages.posfraud"><PosFraud /></ProtectedRoute>} />
                <Route path="/fraud-report" element={<ProtectedRoute permission="pages.fraud_report"><FraudReport /></ProtectedRoute>} />
                <Route path="/dip-safety" element={<ProtectedRoute permission="pages.dip_safety"><PageErrorBoundary><DipSafety /></PageErrorBoundary></ProtectedRoute>} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/infrastructure" element={<ProtectedRoute permission="pages.infrastructure"><Infrastructure /></ProtectedRoute>} />
                <Route path="/video-clips" element={<ProtectedRoute permission="videos.view"><VideoClips /></ProtectedRoute>} />
                <Route path="/monitoring" element={<ProtectedRoute permission="pages.monitoring"><Monitoring /></ProtectedRoute>} />
                <Route path="/reviews" element={<ProtectedRoute permission="pages.reports"><Reviews /></ProtectedRoute>} />
                <Route path="/reviews/:siteId/:scanHour" element={<ProtectedRoute permission="pages.reports"><ScanReviewPage /></ProtectedRoute>} />
                <Route path="/reviews/dip/:reviewId" element={<ProtectedRoute permission="pages.reports"><DipReviewPage /></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute permission="admin.users"><Admin /></ProtectedRoute>} />
                <Route path="/email-log" element={<ProtectedRoute permission="pages.email"><EmailLog /></ProtectedRoute>} />
                <Route path="/activity-log" element={<ProtectedRoute permission="admin.audit"><AuditLog /></ProtectedRoute>} />
                <Route path="/assets" element={<PageErrorBoundary><AssetScanner /></PageErrorBoundary>} />
                <Route path="/assets/:siteId" element={<PageErrorBoundary><AssetScanner /></PageErrorBoundary>} />
                <Route path="/asset-types" element={<ProtectedRoute permission="assets.review"><AssetTypes /></ProtectedRoute>} />
                <Route path="/fixture-schedules" element={<ProtectedRoute permission="pages.schedules"><FixtureSchedules /></ProtectedRoute>} />
                <Route path="/asset-review" element={<ProtectedRoute permission="assets.review"><AssetReview /></ProtectedRoute>} />
                <Route path="/contacts" element={<ProtectedRoute permission="pages.contacts"><Contacts /></ProtectedRoute>} />
                <Route path="/workers" element={<ProtectedRoute permission="pages.infrastructure"><Workers /></ProtectedRoute>} />
                <Route path="/nvr-guide" element={<ProtectedRoute permission="pages.infrastructure"><NvrConnectionGuide /></ProtectedRoute>} />
                <Route path="/help/:slug" element={<HelpPage />} />
                <Route path="*" element={<Navigate to="/" />} />
            </Routes>
        </Layout>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AuthenticatedTemplate>
                <AuthProvider>
                    <AppRoutes />
                </AuthProvider>
            </AuthenticatedTemplate>
            <UnauthenticatedTemplate>
                <Login />
            </UnauthenticatedTemplate>
        </BrowserRouter>
    );
}

export default App;

